/**
 * Product Card Setup (ISS:04-019)
 * Standardized setup pattern (Pattern B)
 */

import { getEventScope } from "../../core/event-scope/event-scope";
import { ProductCardListener } from "./product-card-listener";
import { productCardForm } from "./product-card-form";
import { ProductCardTruncationUtils } from "./product-card-truncation-utils";
import { ProductLikeApiService } from "./like/product-like-api";
import { ProductLikeHandler } from "./like/product-like-handler";
import { ProductLikeRender } from "./like/product-like-render";
import { ImageZoomSetup } from "../../components/controls/image-zoom/image-zoom-setup";
import { CommonUtils } from "../../util/common-utils";
import { ProductCardImageErrorListener } from "./image-error/product-card-image-error-listener";

type ProductCardSetupResult = {
    scope: "product-card";
};

class ProductCardSetup {
    public static syncListImageZoomState(root: ParentNode = document): void {
        const scopeRoot = root instanceof HTMLElement || root instanceof Document ? root : document;
        const toggle = productCardForm.getListImageZoomToggle(scopeRoot);
        const isMobile = CommonUtils.isMobileViewport(768);
        const enabled = (toggle instanceof HTMLInputElement ? toggle.checked : false) && !isMobile;

        productCardForm.getListingCards(scopeRoot).forEach((cardEl) => {
            cardEl.dataset.listImageZoomEnabled = enabled ? "1" : "0";
        });
    }

    /**
     * Initialize product card behaviors (truncation + likes toggle)
     */
    public static init(): ProductCardSetupResult | null {
        if (!productCardForm.hasProductCardOrLikeButton(document)) {
            return null;
        }

        const scope = getEventScope(document, "product-card");

        scope.once("product-card-init", () => {
            ProductCardTruncationUtils.mark(document);
            ImageZoomSetup.init(document);
            ProductCardSetup.syncListImageZoomState(document);
            new ProductCardImageErrorListener().setup(document);

            const likeHandler = new ProductLikeHandler(ProductLikeApiService, ProductLikeRender);

            const handler = {
                handleLike: (btn: HTMLElement) => likeHandler.handleLike(btn),
            };
            const listener = new ProductCardListener(handler, {
                onResize: () => ProductCardTruncationUtils.mark(document),
                onListZoomToggle: () => ProductCardSetup.syncListImageZoomState(document),
            });

            listener.setup();
        });

        return { scope: "product-card" };
    }
}

function initProductCardSystem(): void {
    ProductCardSetup.init();
}

export { ProductCardSetup, initProductCardSystem };
